import { CODE_WORLD_CATALOGUE, type WorldCatalogue } from '@vrlearning/lesson-schema';
import { MANIFEST, worldUrl, type Manifest } from '../worlds/manifest.js';
import { recordingFile } from './files.js';
import {
  dbToGain,
  emitterPosition,
  initialStates,
  levelAt,
  type BurstSource,
  type Layer,
  type SoundPlan,
  type Vec3,
  type Wobble,
} from './plan.js';
import { burstTakes, seedOf, seeded, synthBuffer, synthRate, type Random } from './synth.js';

/**
 * One world's sound, playing (design, "Layers"): built from its plan when the
 * lesson starts, under the engine's ambient bus, and taken down with the
 * lesson. Every node is made here, once; a state change only schedules the
 * params it has (`setTargetAtTime`), and a recording that arrives later joins
 * with a 2 s fade. Emitters are `PannerNode`s placed once at their world
 * position, `inverse` with `refDistance` their distance from the stage's
 * centre, so each is heard at its plan's level on the stage and from where it
 * really is.
 */

/** Recordings are normalised to this at import; one that is not is brought here, within limits. */
const REFERENCE_LUFS = -24;
const TRIM_LIMITS = [-6, 8] as const;
/** A recording that arrives late fades in over this. */
const JOIN_SECONDS = 2;
/** The world's sound comes up with its picture (the 1.5 s fade-in, ADR-032). */
const START_SECONDS = 1.5;
/** A state snapped to at a fade's black: quick, but never a click. */
const SNAP_CONSTANT = 0.03;
/**
 * An eruption's bursts are scheduled this far ahead on the audio clock, topped
 * up by a timer this often: never per frame, and a timer held back (a hidden
 * page) only means a gap, never a backlog.
 */
const BURST_AHEAD = 6;
const BURST_TOP_UP_MS = 1000;
/** The first burst after the eruption begins: seconds, drawn between. */
const BURST_FIRST = [1, 2.5] as const;

export type SoundscapeOptions = {
  readonly world: string;
  /** The lesson's world, as a catalogue: where its features are (a package's, or the code world's). The headset always passes it. */
  readonly worlds?: WorldCatalogue;
  readonly plan: SoundPlan;
  readonly manifest?: Manifest;
  /** Where a stored file is fetched from; `worldUrl` for the code worlds, a package's own URLs. */
  readonly urlOf?: (stored: string) => string;
  /** A world file's bytes; absent, recordings stay silent (a test, a browser that cannot). */
  readonly fetchFile?: (url: string) => Promise<ArrayBuffer>;
  /**
   * Said once per recording that could not be had (`fetch`: offline, not
   * fetched) or that this browser cannot decode (`decode`); never while the
   * soundscape is being built.
   */
  readonly onMissing?: (id: string, reason: 'fetch' | 'decode') => void;
};

type Emitter = { panner?: PannerNode; base: Vec3; source?: AudioBufferSourceNode };

/** An eruption's bursts (plan.ts, `BurstSource`), scheduled one after another on one voice. */
type Bursts = {
  readonly source: BurstSource;
  /** Each burst's own level, set as it starts: they never overlap, so one gain carries them all. */
  readonly level: GainNode;
  readonly booms: AudioBuffer[];
  readonly pops: AudioBuffer[];
  readonly random: Random;
  /** Started, or still to start, and not yet over. */
  readonly scheduled: { source: AudioBufferSourceNode; ends: number; starts: number }[];
  active: boolean;
  /** Where the next burst may start, and when the next boom is due. */
  cursor: number;
  nextBoom: number;
};

type Playing = {
  readonly layer: Layer;
  readonly bursts?: Bursts;
  /** The state's level. */
  readonly gain: GainNode;
  /** In at once for a synthesised layer; a recording fades in when it arrives. */
  readonly entry: GainNode;
  readonly filter?: BiquadFilterNode;
  readonly emitters: Emitter[];
  /** The state it was last set to (a kept state changes nothing). */
  state?: string;
  trimDb: number;
};

export class Soundscape {
  readonly #context: AudioContext;
  readonly #out: GainNode;
  readonly #playing: Playing[] = [];
  readonly #oscillators: OscillatorNode[] = [];
  /** Every node it made, to let go of them all. */
  readonly #nodes: AudioNode[] = [];
  #disposed = false;
  #missing = 0;
  #burstTimer: ReturnType<typeof setInterval> | undefined;
  readonly world: string;
  readonly #worlds: WorldCatalogue;

  constructor(context: AudioContext, destination: AudioNode, options: SoundscapeOptions) {
    this.#context = context;
    this.world = options.world;
    this.#worlds = options.worlds ?? CODE_WORLD_CATALOGUE;
    this.#out = context.createGain();
    this.#out.gain.value = 0;
    this.#out.connect(destination);
    const manifest = options.manifest ?? MANIFEST;
    for (const layer of options.plan.layers) this.#playing.push(this.#build(layer, manifest));
    this.setStates(initialStates(options.world, this.#worlds), false);
    for (const playing of this.#playing) {
      if (playing.layer.source.kind === 'recording') this.#record(playing, manifest, options);
    }
  }

  /** Sources it plays, the silent included: what the Quest's audio thread renders. */
  get voices(): number {
    return this.#playing.reduce(
      (sum, p) => sum + p.emitters.filter((e) => e.source).length + (p.bursts?.active ? 1 : 0),
      0,
    );
  }

  get hrtf(): number {
    return this.#playing.reduce(
      (sum, p) => sum + p.emitters.filter((e) => e.panner?.panningModel === 'HRTF').length,
      0,
    );
  }

  /** Recordings that could not be had or decoded. */
  get missing(): number {
    return this.#missing;
  }

  /** Comes up with the picture: the world fading in. */
  start(): void {
    const now = this.#context.currentTime;
    this.#out.gain.cancelScheduledValues(now);
    this.#out.gain.setValueAtTime(this.#out.gain.value, now);
    this.#out.gain.linearRampToValueAtTime(1, now + START_SECONDS);
  }

  /**
   * Its features' states at a step (lesson-schema's worldStateAt), as the
   * look takes them: eased over the feature's seconds when the class moved on
   * by one, at once otherwise; a kept state changes nothing.
   */
  setStates(states: ReadonlyMap<string, string>, animate: boolean): void {
    if (this.#disposed) return;
    const now = this.#context.currentTime;
    for (const playing of this.#playing) {
      const follows = playing.layer.follows;
      if (!follows) {
        if (playing.state === undefined) {
          playing.state = '';
          setAt(playing.gain.gain, this.#gainOf(playing, states), now, SNAP_CONSTANT);
        }
        continue;
      }
      const state = states.get(follows.feature);
      if (state === undefined || state === playing.state) continue;
      playing.state = state;
      const seconds =
        typeof follows.seconds === 'object'
          ? (follows.seconds[state] ?? 3)
          : (follows.seconds ?? 3);
      const at = animate ? now + (follows.delay ?? 0) : now;
      // A time constant a third of the change: 95 % there when it should be.
      const constant = animate ? seconds / 3 : SNAP_CONSTANT;
      setAt(playing.gain.gain, this.#gainOf(playing, states), at, constant);
      const frequency = follows.frequency?.[state];
      if (frequency !== undefined && playing.filter) {
        setAt(playing.filter.frequency, frequency, at, constant);
      }
      if (playing.bursts)
        this.#burstsFrom(playing, levelAt(playing.layer, states) !== undefined, at);
      const shift = follows.shift?.[state];
      if (shift) {
        for (const emitter of playing.emitters) {
          if (!emitter.panner) continue;
          setAt(emitter.panner.positionX, emitter.base[0] + shift[0], at, constant);
          setAt(emitter.panner.positionY, emitter.base[1] + shift[1], at, constant);
          setAt(emitter.panner.positionZ, emitter.base[2] + shift[2], at, constant);
        }
      }
    }
  }

  /**
   * Fades out in the fade to black, then stops every source and lets go of
   * every node and buffer. The engine's context, buses and switches stay.
   */
  dispose(seconds = 0.3): void {
    if (this.#disposed) return;
    this.#disposed = true;
    const now = this.#context.currentTime;
    this.#out.gain.cancelScheduledValues(now);
    this.#out.gain.setValueAtTime(this.#out.gain.value, now);
    this.#out.gain.linearRampToValueAtTime(0, now + seconds);
    const stopAt = now + seconds + 0.05;
    for (const playing of this.#playing) {
      for (const emitter of playing.emitters) {
        emitter.source?.stop(stopAt);
      }
    }
    for (const oscillator of this.#oscillators) oscillator.stop(stopAt);
    for (const playing of this.#playing) {
      if (!playing.bursts) continue;
      playing.bursts.active = false;
      for (const burst of playing.bursts.scheduled) burst.source.stop(stopAt);
    }
    if (this.#burstTimer !== undefined) clearInterval(this.#burstTimer);
    this.#burstTimer = undefined;
    const disconnect = (): void => {
      for (const node of this.#nodes) node.disconnect();
      this.#nodes.length = 0;
      this.#playing.length = 0;
      this.#oscillators.length = 0;
      this.#out.disconnect();
    };
    // After the fade: disconnecting now would cut the sound off mid-fade.
    if (seconds > 0) setTimeout(disconnect, (seconds + 0.1) * 1000);
    else disconnect();
  }

  #node<T extends AudioNode>(node: T): T {
    this.#nodes.push(node);
    return node;
  }

  #gainOf(playing: Playing, states: ReadonlyMap<string, string>): number {
    const level = levelAt(playing.layer, states);
    return level === undefined ? 0 : dbToGain(level + playing.trimDb);
  }

  #build(layer: Layer, manifest: Manifest): Playing {
    const context = this.#context;
    const gain = this.#node(context.createGain());
    gain.gain.value = 0;
    const entry = this.#node(context.createGain());
    entry.gain.value = layer.source.kind === 'recording' ? 0 : 1;
    gain.connect(entry);
    entry.connect(this.#out);

    // Gusts: a second gain, wobbled round 1 by slow oscillators at unrelated rates.
    let into: AudioNode = gain;
    const source = layer.source;
    if (source.kind === 'noise' && source.gusts) {
      const gust = this.#node(context.createGain());
      gust.gain.value = 1;
      gust.connect(gain);
      this.#wobble(source.gusts, gust.gain, 1 - dbToGain(-source.gusts.depth));
      into = gust;
    }
    let filter: BiquadFilterNode | undefined;
    if (source.kind === 'noise' && source.filter) {
      filter = this.#node(context.createBiquadFilter());
      filter.type = source.filter.type;
      filter.frequency.value = source.filter.frequency;
      filter.Q.value = source.filter.q ?? Math.SQRT1_2;
      filter.connect(into);
      if (source.sweep)
        this.#wobble(source.sweep, filter.frequency, source.filter.frequency * source.sweep.depth);
      into = filter;
    }

    const buffer =
      source.kind === 'recording' || source.kind === 'bursts'
        ? undefined
        : this.#synthBuffer(layer, source);
    const emitters: Emitter[] = [];
    const count = Math.max(1, layer.emitters.length);
    for (let i = 0; i < count; i++) {
      const plan = layer.emitters[i];
      const emitter: Emitter = { base: [0, 0, 0] };
      let input: AudioNode = into;
      if (plan) {
        const base = emitterPosition(this.world, plan, this.#worlds);
        const panner = this.#node(context.createPanner());
        panner.panningModel = layer.hrtf ? 'HRTF' : 'equalpower';
        panner.distanceModel = 'inverse';
        // Its plan's level anywhere on the stage, and its direction real.
        panner.refDistance = Math.max(1, Math.hypot(base[0], base[1], base[2]));
        panner.rolloffFactor = 1;
        panner.maxDistance = 10_000;
        panner.positionX.value = base[0];
        panner.positionY.value = base[1];
        panner.positionZ.value = base[2];
        panner.connect(into);
        emitter.panner = panner;
        emitter.base = base;
        input = panner;
      }
      if (buffer) emitter.source = this.#play(buffer, input, layer, i, count);
      emitters.push(emitter);
    }
    let bursts: Bursts | undefined;
    if (source.kind === 'bursts') {
      const level = this.#node(context.createGain());
      level.connect(emitters[0]?.panner ?? into);
      const takes = burstTakes(source, seedOf(`${this.world}/${layer.name}`));
      const buffered = (samples: Float32Array<ArrayBuffer>): AudioBuffer => {
        const made = context.createBuffer(1, samples.length, takes.rate);
        made.copyToChannel(samples, 0);
        return made;
      };
      bursts = {
        source,
        level,
        booms: takes.booms.map(buffered),
        pops: takes.pops.map(buffered),
        random: seeded(seedOf(`${this.world}/${layer.name}/when`)),
        scheduled: [],
        active: false,
        cursor: 0,
        nextBoom: 0,
      };
    }
    return {
      layer,
      ...(bursts ? { bursts } : {}),
      gain,
      entry,
      ...(filter ? { filter } : {}),
      emitters,
      trimDb: source.kind === 'recording' ? trimFor(source.id, manifest) : 0,
    };
  }

  /**
   * An eruption's bursts at a state change, from `at` -- when the layer's own
   * level starts to move, 0.76 s after the change is seen: what would start
   * after it is taken back then, and what starts before it or is still
   * sounding then plays on and fades with the layer (each lets go of its
   * nodes when it ends). Heard in the new state, they start again from `at`,
   * the first a second or two after it, as the eruption builds. The timer
   * does the scheduling; nothing is made here.
   */
  #burstsFrom(playing: Playing, heard: boolean, at: number): void {
    const bursts = playing.bursts!;
    for (let i = bursts.scheduled.length - 1; i >= 0; i--) {
      const burst = bursts.scheduled[i]!;
      if (burst.starts < at) continue;
      burst.source.stop(at);
      bursts.scheduled.splice(i, 1);
    }
    bursts.level.gain.cancelScheduledValues(at);
    bursts.active = heard;
    if (heard) {
      const between = (range: readonly [number, number]): number =>
        range[0] + bursts.random() * (range[1] - range[0]);
      bursts.cursor = at + between(BURST_FIRST);
      bursts.nextBoom = at + between(bursts.source.booms);
      setTimeout(() => this.#topUp(), 0);
      this.#burstTimer ??= setInterval(() => this.#topUp(), BURST_TOP_UP_MS);
    } else if (!this.#playing.some((p) => p.bursts?.active) && this.#burstTimer !== undefined) {
      clearInterval(this.#burstTimer);
      this.#burstTimer = undefined;
    }
  }

  /** Schedules each erupting layer's bursts up to `BURST_AHEAD` seconds from now, one after another. */
  #topUp(): void {
    if (this.#disposed) return;
    const now = this.#context.currentTime;
    for (const playing of this.#playing) {
      const bursts = playing.bursts;
      if (!bursts?.active) continue;
      const { source, random } = bursts;
      const between = (range: readonly [number, number]): number =>
        range[0] + random() * (range[1] - range[0]);
      // Let go of what has played.
      for (let i = bursts.scheduled.length - 1; i >= 0; i--) {
        const burst = bursts.scheduled[i]!;
        if (burst.ends >= now) continue;
        burst.source.disconnect();
        bursts.scheduled.splice(i, 1);
      }
      // Held back (a hidden page): start afresh from now, never catching up.
      if (bursts.cursor < now) {
        bursts.cursor = now + between([0.3, 1]);
        bursts.nextBoom = Math.max(bursts.nextBoom, bursts.cursor);
      }
      while (bursts.cursor < now + BURST_AHEAD) {
        const pop = bursts.pops[0]!.duration;
        if (bursts.cursor + pop / (1 - source.spread.pitch) <= bursts.nextBoom) {
          const ends = this.#burst(bursts, bursts.pops, bursts.cursor);
          bursts.cursor = ends + between(source.pops);
        } else {
          const at = Math.max(bursts.nextBoom, bursts.cursor);
          const ends = this.#burst(bursts, bursts.booms, at);
          bursts.cursor = ends + between(source.pops);
          bursts.nextBoom = at + between(source.booms);
        }
      }
    }
  }

  /** One burst: a take drawn from `takes`, its own pitch and level, started at `at`. Returns when it ends. */
  #burst(bursts: Bursts, takes: AudioBuffer[], at: number): number {
    const { random, source: plan } = bursts;
    const take = takes[Math.floor(random() * takes.length)]!;
    const source = this.#context.createBufferSource();
    source.buffer = take;
    source.playbackRate.value = 1 + (random() * 2 - 1) * plan.spread.pitch;
    source.connect(bursts.level);
    bursts.level.gain.setValueAtTime(dbToGain((random() * 2 - 1) * plan.spread.db), at);
    source.start(at);
    const ends = at + take.duration / source.playbackRate.value;
    const burst = { source, starts: at, ends };
    bursts.scheduled.push(burst);
    // Played, or taken back: its nodes let go of.
    source.onended = () => {
      source.disconnect();
      const index = bursts.scheduled.indexOf(burst);
      if (index >= 0) bursts.scheduled.splice(index, 1);
    };
    return ends;
  }

  #synthBuffer(
    layer: Layer,
    source: Exclude<Layer['source'], { kind: 'recording' | 'bursts' }>,
  ): AudioBuffer {
    const samples = synthBuffer(source, seedOf(`${this.world}/${layer.name}`));
    const buffer = this.#context.createBuffer(1, samples.length, synthRate(source));
    buffer.copyToChannel(samples, 0);
    return buffer;
  }

  /** One emitter's source: looping, from its own point in the loop, at its own rate. */
  #play(
    buffer: AudioBuffer,
    into: AudioNode,
    layer: Layer,
    index: number,
    count: number,
  ): AudioBufferSourceNode {
    const source = this.#node(this.#context.createBufferSource());
    source.buffer = buffer;
    source.loop = true;
    source.playbackRate.value = layer.rates?.[index] ?? 1;
    source.connect(into);
    source.start(0, (buffer.duration * index) / count);
    return source;
  }

  #wobble(wobble: Wobble, param: AudioParam, depth: number): void {
    const share = depth / wobble.rates.length;
    for (const rate of wobble.rates) {
      const oscillator = this.#node(this.#context.createOscillator());
      oscillator.frequency.value = rate;
      const amount = this.#node(this.#context.createGain());
      amount.gain.value = share;
      oscillator.connect(amount);
      amount.connect(param);
      oscillator.start();
      this.#oscillators.push(oscillator);
    }
  }

  #record(playing: Playing, manifest: Manifest, options: SoundscapeOptions): void {
    const source = playing.layer.source;
    if (source.kind !== 'recording') return;
    const miss = (reason: 'fetch' | 'decode'): void => {
      if (this.#disposed) return;
      this.#missing++;
      options.onMissing?.(source.id, reason);
    };
    const fetchFile = options.fetchFile;
    if (!fetchFile) return;
    let file;
    try {
      file = recordingFile(source.id, manifest);
    } catch {
      // Told after the constructor: whoever made it is still setting up.
      queueMicrotask(() => miss('fetch'));
      return;
    }
    void fetchFile((options.urlOf ?? worldUrl)(file.stored)).then(
      (bytes) =>
        this.#context.decodeAudioData(bytes).then(
          (buffer) => {
            if (this.#disposed) return;
            const count = playing.emitters.length;
            playing.emitters.forEach((emitter, i) => {
              const into = emitter.panner ?? playing.gain;
              emitter.source = this.#play(buffer, into, playing.layer, i, count);
            });
            const now = this.#context.currentTime;
            playing.entry.gain.setValueAtTime(0, now);
            playing.entry.gain.linearRampToValueAtTime(1, now + JOIN_SECONDS);
          },
          () => miss('decode'),
        ),
      () => miss('fetch'),
    );
  }
}

/** A recording's trim to the reference: one held under its peak at import is brought up, within limits. */
function trimFor(id: string, manifest: Manifest): number {
  let lufs = REFERENCE_LUFS;
  try {
    lufs = recordingFile(id, manifest).lufs ?? REFERENCE_LUFS;
  } catch {
    // Missing from the manifest: said when it is fetched (#record).
  }
  const [low, high] = TRIM_LIMITS;
  return Math.min(high, Math.max(low, REFERENCE_LUFS - lufs));
}

/** Heads for `value` from `at`, with its time constant; what was scheduled after `at` is dropped. */
function setAt(param: AudioParam, value: number, at: number, constant: number): void {
  param.cancelScheduledValues(at);
  param.setTargetAtTime(value, at, constant);
}
